import React, { useEffect, useState } from 'react';
import { useLocalizationContext } from '@amzn/react-arb-tools';
import I18nProvider, {
  importMessages,
  type I18nProviderProps,
} from '@amzn/awsui-components-console/i18n';
import { toCloudscapeLocale } from 'src/i18n/cloudscapeLocales';

/**
 * Localizes the strings Cloudscape owns — pagination aria-labels, sort hints, the date-range
 * picker's whole interface — none of which routes through `strings.puff.json`.
 *
 * Packs load on demand because each of Cloudscape's 15 locales is a ~48 KB JSON pack, and the load
 * must not gate the tree: a missing pack is cosmetic (each component falls through to its built-in
 * English), so children render immediately rather than blanking on every locale change.
 *
 * Mounted INSIDE `LocalizationContextProvider` so a locale change re-renders it; outside, it would
 * read the locale once and never update.
 */
const CloudscapeI18nProvider: React.FC<{
  readonly children?: React.ReactNode;
}> = ({ children }) => {
  const { localizationContext } = useLocalizationContext();
  // The mapped tag — never our own — is what both the loader and the provider below receive; see
  // `toCloudscapeLocale`.
  const cloudscapeLocale = toCloudscapeLocale(localizationContext.getLocale());
  const [messages, setMessages] = useState<
    ReadonlyArray<I18nProviderProps.Messages>
  >([]);

  useEffect(() => {
    let cancelled = false;
    // Cleared before the load so the tree renders Cloudscape's built-in English while the pack is
    // pending or if it fails, rather than the previous locale's strings under the new `locale`.
    setMessages([]);
    void importMessages(cloudscapeLocale)
      .then((loaded) => {
        // Guards a late resolution from a superseded locale overwriting a newer one, and a set
        // after unmount.
        if (!cancelled) {
          setMessages(loaded);
        }
      })
      // Swallowed: there is no user-actionable failure here and no localized copy to report it
      // with; the reset above already selected the English fallback.
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [cloudscapeLocale]);

  return (
    <I18nProvider locale={cloudscapeLocale} messages={messages}>
      {children}
    </I18nProvider>
  );
};

export default CloudscapeI18nProvider;
